import React, { Component, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import withRouter from 'utils/withRouter';
import { bool, func, object } from 'prop-types';
import queryString from 'query-string';

import { get, isEmpty } from 'ravenjs/utils/lodash';

import Acl from 'modules/acl';
import { actions as pendoActions } from 'modules/pendo';
import { ARIES_AREAS_EXPERTISE } from 'constants/ai';
import { DEFAULT_COLORS } from 'constants/colors';
import { APP_PERMISSIONS } from 'constants/permissions';
import { ROOT_PRIVATE_ROUTE, ROOT_PRIVATE_ROUTE_PLATFORM_LITE } from 'constants/routes';
import { INFO } from 'constants/version';
import { CLICK_TO_BUY } from 'constants/clickToBuy';
import { MODALS } from 'constants/modals';
import { EVENTS } from 'constants/events';
import { actions as inProductMarketingActions } from 'modules/inProductMarketing';
import { selectors as authSelectors } from 'modules/auth';
import { selectors as userSelectors } from 'modules/user';
import { actions as uiActions } from 'modules/ui';
import {
    buildPayloadForInProductMarketing,
    cleanSafetyLocalStorage,
    hasFeatureFlag,
    setPreviousSafetyLocalStorage,
} from 'utils/common';
import { getOnClickBackCallback, hasSearch, pushesToSearchPage } from 'utils/search';
import ContentSearch from 'components/ContentSearch';
import HelpCenterSearchTypeAhead from 'components/HelpCenterSearchTypeAhead';

import { actions as brandingActions, selectors as brandingSelectors } from 'modules/branding';

import { pauseHotjarScript, resumeHotjarScript } from 'utils/hotjar';
import AuthRoute from 'routes/AuthRoute';

function MyTicketsRedirect() {
    const location = useLocation();
    const newPath = location.pathname.replace('/my-tickets', '/my-cases') + location.search;
    return <Navigate to={newPath} replace />;
}

function ClickToBuyRouter() {
    const location = useLocation();
    const queryParams = queryString.parse(location.search);
    const { productCode } = queryParams;
    const productCodeTrimmed = productCode ? productCode.trim() : '';
    const smartHandbookPlusProductKey = CLICK_TO_BUY.SEHP.SMART_HANDBOOK.QUOTE_KEY.trim();
    const whpProductKey = CLICK_TO_BUY.WHP.WHP_UPGRADE.QUOTE_KEY.trim();

    if (smartHandbookPlusProductKey.includes(productCodeTrimmed)) {
        return <PaymentConfirmation type={CLICK_TO_BUY.COMMON.TYPES.SEH_PLUS} />;
    } else if (whpProductKey.includes(productCodeTrimmed)) {
        return <PaymentConfirmation type={CLICK_TO_BUY.COMMON.TYPES.WHP} />;
    }
    return null;
}

// Lazily load in all of the routes.
const Admin = lazy(() => import('../containers/Admin'));
const AIAssistant = lazy(() => import('../containers/AIAssistant'));
const Auth = lazy(() => import('../containers/Auth'));
const Dashboard = lazy(() => import('../containers/Dashboard'));
const DHEAudits = lazy(() => import('../containers/DHEAudits'));
const GuidedHRAssessment = lazy(() => import('../containers/GuidedHRAssessment'));
const HRTools = lazy(() => import('../containers/HRTools'));
const Impersonation = lazy(() => import('../containers/Impersonation'));
const LegalAndPrivacy = lazy(() => import('../containers/LegalAndPrivacy'));
const Profile = lazy(() => import('../containers/Profile'));
const Compliance = lazy(() => import('../containers/Compliance'));
const MineralIntelligence = lazy(() => import('../containers/MineralIntelligence'));
const MyTickets = lazy(() => import('../containers/MyTickets'));
const Tools = lazy(() => import('../containers/Tools'));
const Reporting = lazy(() => import('../containers/Reporting'));
const Search = lazy(() => import('../containers/Search'));
const FutureWork = lazy(() => import('../containers/FutureWork'));
const Templates = lazy(() => import('../containers/Templates'));
const Notifications = lazy(() => import('../containers/Notifications'));
const CompanyPolicies = lazy(() => import('../containers/CompanyPolicies'));
const Document = lazy(() => import('../containers/Document'));
const Public = lazy(() => import('../containers/Public'));
const MindmatrixSSO = lazy(() => import('../containers/MindmatrixSSO'));
const Prospect = lazy(() => import('../containers/Prospect'));
const Resources = lazy(() => import('../containers/Resources'));
const Safety = lazy(() => import('../containers/Safety'));
const TodoList = lazy(() => import('../containers/TodoList'));
const Training = lazy(() => import('../containers/Training'));
const HelpCenter = lazy(() => import('../containers/HelpCenter'));
const CaseCave = lazy(() => import('../containers/CaseCave'));
const OpenIdConnect = lazy(() => import('../containers/OpenIdConnect'));
const OpenIdRedirect = lazy(() => import('../containers/OpenIdRedirect'));
const MAPPortal = lazy(() => import('../containers/MAPPortal'));
const ClickToBuyWHP = lazy(() => import('../containers/ClickToBuy/WHP'));
const PaymentConfirmation = lazy(() =>
    import('../containers/ClickToBuy/components/PaymentConfirmation')
);
const Marketplace = lazy(() => import('../containers/ClickToBuy/Marketplace'));
const Community = lazy(() => import('../containers/Community'));

class Routes extends Component {
    constructor(props) {
        super(props);
        const { brandingConfig } = this.props;
        this.updateBranding = this.updateBranding.bind(this);
        if (
            !brandingConfig.sprintTimestamp ||
            brandingConfig.sprintTimestamp < INFO.sprintTimestamp
        ) {
            this.updateBranding();
        }

        this.routeListener(props.history);
    }

    componentDidMount = () => {};

    setTicketingIframeRef = input => {
        this.ticketingiframeRef = input;
    };

    sendEmail = async (pendoId, optyName, source, adType) => {
        const { isImpersonatingUser, loggedUser, sendEmail, sendPendoEvent } = this.props;
        try {
            const { company } = loggedUser;
            const employeeCount = get(company, 'companySize', 0);

            const params = buildPayloadForInProductMarketing(
                loggedUser,
                isImpersonatingUser,
                optyName,
                pendoId.replace('&', ''),
                source,
                adType,
                employeeCount
            );
            await sendEmail({ params });
            const previousSafetyContent = localStorage.getItem('previousSafetyContent')
                ? JSON.parse(localStorage.getItem('previousSafetyContent'))
                : {};
            const pendoEvent = {
                event: 'Sent Email - In-Product Marketing - Lead Success',
                eventProperties: {
                    GuideName: localStorage.getItem('guideName') || previousSafetyContent.guideName,
                    SafetyCategory:
                        localStorage.getItem('safetyCategory') ||
                        previousSafetyContent.safetyCategory,
                    SafetyTopic:
                        localStorage.getItem('safetyTopic') || previousSafetyContent.safetyTopic,
                    SafetyContent:
                        localStorage.getItem('safetyContent') ||
                        previousSafetyContent.safetyContent,
                    Source: localStorage.getItem('source') || previousSafetyContent.source,
                },
            };
            sendPendoEvent(pendoEvent);
            setPreviousSafetyLocalStorage();
            cleanSafetyLocalStorage();
            pendo.showGuideByName('In-Product Marketing - Lead Success');

            if (optyName === 'AssureHire') {
                sendPendoEvent(EVENTS.SOLUTION_SHOP.ADDITION_PRODUCTS.REQUEST_PRICING.ASSURE_HIRE);
            } else if (optyName === 'Trakstar_Perform') {
                sendPendoEvent(
                    EVENTS.SOLUTION_SHOP.ADDITION_PRODUCTS.REQUEST_PRICING.TRAKSTAR_PERFORM
                );
            } else if (optyName === 'Tracker_I9') {
                sendPendoEvent(EVENTS.SOLUTION_SHOP.ADDITION_PRODUCTS.REQUEST_PRICING.TRACKER_I9);
            } else if (optyName === 'GHRC_MNL') {
                sendPendoEvent(EVENTS.SOLUTION_SHOP.CORE_PRODUCTS.REQUEST_PRICING.GHRC);
            } else if (optyName === 'LearnBundle-1_MNL') {
                sendPendoEvent(EVENTS.SOLUTION_SHOP.CORE_PRODUCTS.REQUEST_PRICING.LEARN_CATALOGS);
            } else if (optyName === 'ACA_Reporting_MNL') {
                sendPendoEvent(EVENTS.SOLUTION_SHOP.CORE_PRODUCTS.REQUEST_PRICING.ACA);
            } else if (optyName === 'Mitratech_Ethics_Hotline') {
                sendPendoEvent(
                    EVENTS.SOLUTION_SHOP.ADDITION_PRODUCTS.REQUEST_PRICING.ETHICS_HOTLINE
                );
            } else if (optyName === 'Leave') {
                sendPendoEvent(EVENTS.SOLUTION_SHOP.ADDITION_PRODUCTS.REQUEST_PRICING.LEAVE);
            }
        } catch (e) {
            pendo.showGuideByName('In-Product Marketing - Lead Failure');
        }
    };

    updateBranding = async (pendoId, optyName, source, adType) => {
        const {
            fetchBranding,
            loggedUser: { company },
        } = this.props;
        const partnerId = company && company.partnerId;
        if (partnerId) {
            await fetchBranding(partnerId);
        }
    };

    routeListener = history => {
        history.listen(({ location }) => {
            const locationHash = get(location, 'hash', '');

            const { brandingConfig, openModal, closeModal } = this.props;
            if (
                !brandingConfig.sprintTimestamp ||
                brandingConfig.sprintTimestamp < INFO.sprintTimestamp
            ) {
                this.updateBranding();
            }

            if (location.pathname.includes('/ticketing')) {
                this.ticketingiframeRef &&
                    this.ticketingiframeRef.sendMessage({ route: location.pathname }, ['*']);
            } else if (!isEmpty(locationHash) && locationHash.includes('OptyName')) {
                const guideId = locationHash.split('OptyName=');
                const queryParamsParse = queryString.parse(locationHash);
                const source = queryParamsParse ? queryParamsParse.Source : '';
                const adType = queryParamsParse ? queryParamsParse.AdType : '';
                const optyName = queryParamsParse ? queryParamsParse.OptyName : '';

                window.history.replaceState(
                    null,
                    document.title,
                    window.location.pathname + window.location.search
                );

                this.sendEmail(get(guideId, '0', ''), optyName, source, adType);
            } else if (!isEmpty(locationHash) && locationHash.includes('ariesAreasOfExpertise')) {
                openModal(MODALS.ARIES_INFORMATION, {
                    title: ARIES_AREAS_EXPERTISE.TITLE,
                    showTitle: true,
                    hideIcon: true,
                    paddingContainer: '0.25rem',
                    description: ARIES_AREAS_EXPERTISE.DESCRIPTION,
                    ModalProps: {
                        ContentProps: {
                            style: {
                                maxWidth: '520px',
                                margin: '1rem',
                                padding: '2.5rem',
                                borderRadius: '0.5rem',
                                backgroundColor: DEFAULT_COLORS.WHITE,
                            },
                        },
                    },
                    modalProps: {
                        headerProps: {
                            padding: '0 0 0.5rem 0',
                            borderWidth: '0',
                            fontSize: '1.5rem',
                        },
                        bodyProps: {
                            padding: '0',
                        },
                        footerProps: {
                            padding: '0',
                            justifyContent: 'flex-start !important',
                        },
                    },
                    actions: [
                        {
                            text: ARIES_AREAS_EXPERTISE.LABEL_BUTTON,
                            color: 'primary',
                            padding: '0.844rem 1.5rem',
                            style: {
                                fontSize: '1rem',
                                fontFamily: 'Favorit',
                                borderRadius: '2px',
                                minWidth: 'auto',
                                marginRight: '26px',
                            },
                            onClick() {
                                closeModal(MODALS.ARIES_INFORMATION);
                                window.history.replaceState(
                                    null,
                                    document.title,
                                    window.location.pathname + window.location.search
                                );
                            },
                        },
                    ],
                });
            }
        });
    };

    render() {
        const { history } = this.props;
        const isFullScreenMode = hasFeatureFlag(history.location, 'showFullScreenMode');

        const p = get(history, 'location.pathname', null);

        if (!p.startsWith('/help-center')) {
            pauseHotjarScript();
        } else {
            resumeHotjarScript();
        }

        const defaultLandingPageRoute = Acl.check(APP_PERMISSIONS.platformliteCompanyDashboardView)
            ? ROOT_PRIVATE_ROUTE_PLATFORM_LITE
            : ROOT_PRIVATE_ROUTE;
        return (
            <Suspense fallback={<div />}>
                {hasSearch(p) && !isFullScreenMode && (
                    <ContentSearch
                        enablePushToSearchPage={pushesToSearchPage(p)}
                        onClickBack={getOnClickBackCallback(history)}
                    />
                )}
                {history.location.pathname.includes('/help-center') && (
                    <HelpCenterSearchTypeAhead history={history} />
                )}
                <RouterRoutes>
                    <Route
                        path="/auth/*"
                        element={
                            <AuthRoute isPublic>
                                <Auth />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/aries/*"
                        element={
                            <AuthRoute>
                                <AIAssistant />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/openid/redirect/*"
                        element={
                            <AuthRoute isPublic>
                                <OpenIdRedirect />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/openid/*"
                        element={
                            <AuthRoute isPublic>
                                <OpenIdConnect />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/dashboard/*"
                        element={
                            <AuthRoute>
                                <Dashboard />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/dhe-audits/*"
                        element={
                            <AuthRoute>
                                <DHEAudits />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/help-center/*"
                        element={
                            <AuthRoute>
                                <HelpCenter />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/hr-tools/*"
                        element={
                            <AuthRoute>
                                <HRTools />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/partner-enablement/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.resources}>
                                <MindmatrixSSO />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/profile/*"
                        element={
                            <AuthRoute>
                                <Profile />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/admin/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.system}>
                                <Admin />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/legal-and-privacy/*"
                        element={
                            <AuthRoute>
                                <LegalAndPrivacy />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/training/:section/category/:categoryId/:categoryName/:subcategoryId/:subcategory/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.training}>
                                <Training />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/training/:section/category/:categoryId/:categoryName/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.training}>
                                <Training />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/training/:section/:sectionId/:extraSectionId/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.training}>
                                <Training />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/training/:section/:sectionId/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.training}>
                                <Training />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/training/:section/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.training}>
                                <Training />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/impersonation/:action/*"
                        element={
                            <AuthRoute>
                                <Impersonation />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/guided-hr-assessment/*"
                        element={
                            <AuthRoute>
                                <GuidedHRAssessment />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/hr-compliance/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.compliance}>
                                <Compliance />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/featured-content/:id/details/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.compliance}>
                                <Document />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/mineral-intelligence/*"
                        element={
                            <AuthRoute>
                                <MineralIntelligence />
                            </AuthRoute>
                        }
                    />
                    <Route path="/ticketing/*" element={<Navigate to="/case-cave" replace />} />
                    <Route
                        path="/case-cave/prepped_answers/*"
                        element={<Navigate to="/case-cave" replace />}
                    />
                    <Route
                        path="/case-cave/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.systemTicketingSystemView}>
                                <CaseCave setTicketingIframeRef={this.setTicketingIframeRef} />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/my-cases/*"
                        element={
                            <AuthRoute>
                                <MyTickets />
                            </AuthRoute>
                        }
                    />
                    <Route path="/my-tickets/*" element={<MyTicketsRedirect />} />
                    <Route
                        path="/tools/*"
                        element={
                            <AuthRoute>
                                <Tools />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/reporting/*"
                        element={
                            <AuthRoute>
                                <Reporting />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/search/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.compliance}>
                                <Search />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/map-portal/*"
                        element={
                            <AuthRoute
                                permissions={APP_PERMISSIONS.mineralAdvantageProgramDashboardView}
                            >
                                <MAPPortal />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/futurework/*"
                        element={
                            <AuthRoute>
                                <FutureWork />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/templates/*"
                        element={
                            <AuthRoute>
                                <Templates />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/resources/*"
                        element={
                            <AuthRoute>
                                <Resources />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/notifications/*"
                        element={
                            <AuthRoute>
                                <Notifications />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/company-policies/*"
                        element={
                            <AuthRoute>
                                <CompanyPolicies />
                            </AuthRoute>
                        }
                    />

                    <Route
                        path="/click-to-buy/:productCode?/:invoiceID?"
                        element={
                            <AuthRoute>
                                <ClickToBuyRouter />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/prospect/*"
                        element={
                            <AuthRoute>
                                <Prospect />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/safety/*"
                        element={
                            <AuthRoute>
                                <Safety />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/todo/*"
                        element={
                            <AuthRoute>
                                <TodoList />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/whp/whp-upgrade/*"
                        element={
                            <AuthRoute>
                                <ClickToBuyWHP />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/solution-shop/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.systemMarketplace}>
                                <Marketplace />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/community/*"
                        element={
                            <AuthRoute permissions={APP_PERMISSIONS.community}>
                                <Community />
                            </AuthRoute>
                        }
                    />
                    <Route
                        path="/public/*"
                        element={
                            <AuthRoute isPublic>
                                <Public />
                            </AuthRoute>
                        }
                    />
                    <Route path="/login" element={<Navigate to="/auth/login" replace />} />
                    <Route path="/logout" element={<Navigate to="/auth/logout" replace />} />
                    {/* Redirect all unknown routes to the `dashboard` for now */}
                    <Route path="*" element={<Navigate to={defaultLandingPageRoute} replace />} />
                </RouterRoutes>
            </Suspense>
        );
    }
}

Routes.propTypes = {
    closeModal: func.isRequired,
    brandingConfig: object,
    history: object,
    isImpersonatingUser: bool.isRequired,
    loggedUser: object,
    sendEmail: func.isRequired,
    fetchBranding: func.isRequired,
    openModal: func.isRequired,
    sendPendoEvent: func.isRequired,
};

Routes.defaultProps = {
    brandingConfig: {},
    history: {},
    loggedUser: {},
};

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    sendEmail: inProductMarketingActions.sendEmail,
    fetchBranding: brandingActions.fetchBranding,
    sendPendoEvent: pendoActions.sendPendoEvent,
    openModal: uiActions.openModal,
};

const mapStateToProps = createStructuredSelector({
    isImpersonatingUser: authSelectors.isImpersonatingUser,
    loggedUser: userSelectors.getUser,
    brandingConfig: brandingSelectors.getBranding,
});

export { Routes as RoutesUnwrapped };
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Routes));
